import { useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import {
  Box,
  VStack,
  Flex,
  HStack,
  Heading,
  Skeleton,
  SkeletonText,
  Icon,
} from "@chakra-ui/react";
import CalendarMonth from "components/Icon/Iconify/CalendarMonth";
import { MeetingEvent, MeetingEventsTable, MeetingEventPreview } from "features/MeetingEvents";
import MeetingEventsFiltersComponent from "features/MeetingEvents/Components/MeetingEventsFilters";
import AppPage from "layouts/AppPage";
import { Heading18 } from "components/Heading";
import useSideDrawer from "hooks/useSideDrawer";
import SideDrawer from "components/SideDrawer";
import { SecondaryButton } from "components/Button";
import { Link } from "react-router";
import { AdminMeetingTypeTag } from "design-system";
import { meetingEventPathById } from "helpers/url";
import { MeetingEventsFilters } from "features/MeetingEvents/hooks";
import moment from "moment";

const defaultFilters: MeetingEventsFilters = {
  start_time: moment().startOf("week").format(),
  end_time: moment().endOf("week").format(),
  scheduled_status: "scheduled",
};

function sideDrawerHeader({ item, isLoading }: { item?: MeetingEvent; isLoading?: boolean; }) {
  if (isLoading) {
    return (
      <SkeletonText noOfLines={1} />
    );
  }
  if (!item) return null;

  return (
    <Flex direction="column" align="flex-start" gap="2">
      <HStack w="100%" align="center" justify="space-between" pr="50px">
        <HStack align="center" spacing="2">
          <AdminMeetingTypeTag.IconBadge meetingType={item.meetingType} />
          <Heading
            fontSize="md"
            fontWeight="700"
            lineHeight="19.2px"
          >
            {item.title}
          </Heading>
        </HStack>
        <Flex gap="2">
          <Link to={meetingEventPathById(item.id)}>
            <SecondaryButton size="sm">
              View Details
            </SecondaryButton>
          </Link>
        </Flex>
      </HStack>
    </Flex>
  );
}

function sideDrawer({ item, isLoading }: { item?: MeetingEvent; isLoading?: boolean; }) {
  if (isLoading) {
    return (
      <Skeleton />
    );
  }

  if (!item) return null;

  return (
    <MeetingEventPreview meetingEvent={item} showHeader={false} />
  );
}

export default function MeetingEventsPage() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<MeetingEventsFilters>({
    ...defaultFilters,
    ...(searchParams.get("scheduled_status") === "pending_scheduling" && {
      scheduled_status: "pending_scheduling",
    }),
  });
  const handleFiltersChange = useCallback((newFilters: MeetingEventsFilters) => {
    setFilters(newFilters);
  }, []);

  const {
    isOpen,
    onClose,
    selectedItem,
    expandClickHandlerByItem,
  } = useSideDrawer<MeetingEvent>({ getItemId: (item) => item?.id });

  return (
    <AppPage>
      <HStack my="4" width="100%">
        <Icon as={CalendarMonth} boxSize={5} color="fg.subtle" />
        <Heading18>Meetings</Heading18>
      </HStack>
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <MeetingEventsFiltersComponent
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />

          <MeetingEventsTable
            filters={filters}
            onExpand={expandClickHandlerByItem}
          />
        </VStack>
      </Box>

      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        item={selectedItem}
        header={(props) => sideDrawerHeader(props)}
      >
        {(props) => sideDrawer(props)}
      </SideDrawer>
    </AppPage>
  );
}
